import { useState, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Map, Upload, ExternalLink, ArrowLeft, Save } from 'lucide-react';
import { useBroadcastChannel } from '@/hooks/useBroadcastChannel';
import ControlPanel from '@/components/ControlPanel';
import TokenSidebar from '@/components/TokenSidebar';
import MapUploadDialog from '@/components/MapUploadDialog';
import { Toaster, toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import { assetPreloader, sceneStateCache } from '@/lib/assetCache';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [sessionName, setSessionName] = useState('');
  const [mapUrl, setMapUrl] = useState('');
  const [mapId, setMapId] = useState('');
  const [spotlight, setSpotlight] = useState({ x: 50, y: 50 });
  const [radius, setRadius] = useState(150);
  const [zoom, setZoom] = useState(1);
  const [fogOpacity, setFogOpacity] = useState(1);
  const [feather, setFeather] = useState(40);
  const [locked, setLocked] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(50);
  const [gridColor, setGridColor] = useState('white');
  const [gridOpacity, setGridOpacity] = useState(0.1);
  // 'spotlight' | 'off' | 'fullbright'
  const [spotlightMode, setSpotlightMode] = useState('spotlight');
  const [placedTokens, setPlacedTokens] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [draggingToken, setDraggingToken] = useState(null);
  const [pings, setPings] = useState([]);
  const [showMapDialog, setShowMapDialog] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Scenes
  const [scenes, setScenes] = useState([]);
  const [currentSceneId, setCurrentSceneId] = useState(null);
  const mapRef = useRef(null);
  const worldRef = useRef(null);
  const playerWindowRef = useRef(null);

  // Refs for broadcast
  const stateRefs = useRef({});
  useEffect(() => {
    stateRefs.current = { spotlight, radius, zoom, fogOpacity, feather, mapUrl, placedTokens, showGrid, gridSize, gridColor, gridOpacity, spotlightMode, panOffset };
  }, [spotlight, radius, zoom, fogOpacity, feather, mapUrl, placedTokens, showGrid, gridSize, gridColor, gridOpacity, spotlightMode, panOffset]);

  const onAdminMessage = useCallback((data) => {
    if (data?.type === 'REQUEST_STATE') {
      const s = stateRefs.current;
      const bc = new BroadcastChannel('fog-of-war-channel');
      bc.postMessage({ type: 'FULL_STATE', x: s.spotlight.x, y: s.spotlight.y, ...s });
      bc.close();
    }
  }, []);

  const { postMessage } = useBroadcastChannel(onAdminMessage);

  const broadcastFullState = useCallback(() => {
    postMessage({
      type: 'FULL_STATE', x: spotlight.x, y: spotlight.y,
      radius, zoom, fogOpacity, feather, mapUrl, placedTokens, showGrid, gridSize, gridColor, gridOpacity, spotlightMode, panOffset
    });
  }, [spotlight, radius, zoom, fogOpacity, feather, mapUrl, placedTokens, showGrid, gridSize, gridColor, gridOpacity, spotlightMode, panOffset, postMessage]);

  useEffect(() => { broadcastFullState(); }, [broadcastFullState]);

  // Load session
  useEffect(() => {
    if (!sessionId) return;
    axios.get(`${API}/sessions/${sessionId}`).then(r => {
      const s = r.data;
      setSessionName(s.name || '');
      if (s.map_url) setMapUrl(s.map_url);
      if (s.map_id) setMapId(s.map_id);
      if (s.spotlight) setSpotlight(s.spotlight);
      if (s.radius) setRadius(s.radius);
      if (s.zoom) setZoom(s.zoom);
      if (s.fog_opacity !== undefined) setFogOpacity(s.fog_opacity);
      if (s.feather !== undefined) setFeather(s.feather);
      if (s.grid_size) setGridSize(s.grid_size);
      if (s.grid_color) setGridColor(s.grid_color);
      if (s.grid_opacity !== undefined) setGridOpacity(s.grid_opacity);
      if (s.spotlight_mode) setSpotlightMode(s.spotlight_mode);
      else if (s.spotlight_enabled !== undefined) setSpotlightMode(s.spotlight_enabled ? 'spotlight' : 'off');
      if (s.show_grid !== undefined) setShowGrid(s.show_grid);
      if (s.placed_tokens) setPlacedTokens(s.placed_tokens);
      if (s.current_scene_id) setCurrentSceneId(s.current_scene_id);
    }).catch(() => { toast.error('Session not found'); navigate('/'); });
    // Load scenes + preload their assets
    axios.get(`${API}/sessions/${sessionId}/scenes`).then(r => {
      setScenes(r.data);
      // Background preload assets for all scenes
      assetPreloader.preloadScenes(r.data);
    }).catch(() => {});
  }, [sessionId, navigate]);

  const getSessionPayload = useCallback(() => ({
    map_url: mapUrl, map_id: mapId, spotlight, radius, zoom,
    fog_opacity: fogOpacity, feather, grid_size: gridSize,
    grid_color: gridColor, grid_opacity: gridOpacity,
    spotlight_mode: spotlightMode,
    show_grid: showGrid, placed_tokens: placedTokens,
    current_scene_id: currentSceneId, pan_offset: panOffset,
  }), [mapUrl, mapId, spotlight, radius, zoom, fogOpacity, feather, gridSize, gridColor, gridOpacity, spotlightMode, showGrid, placedTokens, currentSceneId, panOffset]);

  // Auto-save every 10s
  useEffect(() => {
    if (!sessionId) return;
    const timer = setInterval(() => {
      axios.put(`${API}/sessions/${sessionId}`, getSessionPayload()).catch(() => {});
    }, 10000);
    return () => clearInterval(timer);
  }, [sessionId, getSessionPayload]);

  const saveSession = useCallback(() => {
    if (!sessionId) return;
    axios.put(`${API}/sessions/${sessionId}`, getSessionPayload())
      .then(() => toast.success('Session saved')).catch(() => toast.error('Save failed'));
  }, [sessionId, getSessionPayload]);

  // ===== SCENES =====
  const saveCurrentScene = useCallback(async () => {
    if (!currentSceneId) return;
    const sceneData = {
      map_url: mapUrl, spotlight, radius, zoom, fog_opacity: fogOpacity, feather,
      grid_size: gridSize, grid_color: gridColor, grid_opacity: gridOpacity,
      show_grid: showGrid, spotlight_mode: spotlightMode,
      placed_tokens: placedTokens, pan_offset: panOffset,
    };
    // Save to API and IndexedDB in parallel
    await Promise.all([
      axios.put(`${API}/scenes/${currentSceneId}`, sceneData).catch(() => {}),
      sceneStateCache.save(currentSceneId, sceneData),
    ]);
  }, [currentSceneId, mapUrl, spotlight, radius, zoom, fogOpacity, feather, gridSize, gridColor, gridOpacity, showGrid, spotlightMode, placedTokens, panOffset]);

  const applySceneData = useCallback((sc) => {
    if (sc.map_url) setMapUrl(sc.map_url);
    if (sc.spotlight) setSpotlight(sc.spotlight);
    if (sc.radius) setRadius(sc.radius);
    if (sc.zoom) setZoom(sc.zoom);
    if (sc.fog_opacity !== undefined) setFogOpacity(sc.fog_opacity);
    if (sc.feather !== undefined) setFeather(sc.feather);
    if (sc.grid_size) setGridSize(sc.grid_size);
    if (sc.grid_color) setGridColor(sc.grid_color);
    if (sc.grid_opacity !== undefined) setGridOpacity(sc.grid_opacity);
    if (sc.show_grid !== undefined) setShowGrid(sc.show_grid);
    if (sc.spotlight_mode) setSpotlightMode(sc.spotlight_mode);
    if (sc.placed_tokens) setPlacedTokens(sc.placed_tokens);
    if (sc.pan_offset) setPanOffset(sc.pan_offset);
    else setPanOffset({ x: 0, y: 0 });
    setSelectedToken(null);
  }, []);

  const addScene = useCallback(async (name, sceneMapUrl) => {
    const res = await axios.post(`${API}/sessions/${sessionId}/scenes`, {
      name, map_url: sceneMapUrl || mapUrl,
      grid_size: gridSize, grid_color: gridColor, grid_opacity: gridOpacity,
      show_grid: showGrid, spotlight_mode: spotlightMode,
      placed_tokens: [], spotlight: { x: 50, y: 50 },
      radius, zoom: 1, fog_opacity: fogOpacity, feather,
    });
    setScenes(prev => [...prev, res.data]);
    return res.data;
  }, [sessionId, mapUrl, gridSize, gridColor, gridOpacity, showGrid, spotlightMode, radius, fogOpacity, feather]);

  const switchScene = useCallback(async (sceneId) => {
    await saveCurrentScene();
    setCurrentSceneId(sceneId);

    // Try instant load from IndexedDB cache
    const cached = await sceneStateCache.load(sceneId);
    if (cached) {
      applySceneData(cached);
    }

    // Fetch authoritative state from API (updates if different)
    try {
      const res = await axios.get(`${API}/scenes/${sceneId}`);
      const sc = res.data;
      applySceneData(sc);
      // Cache the fresh data
      sceneStateCache.save(sceneId, sc);
      // Preload assets for this scene
      assetPreloader.preloadScene(sc);
      toast.success(`Scene: ${sc.name}`);
    } catch {
      if (!cached) toast.error('Failed to load scene');
    }
  }, [saveCurrentScene, applySceneData]);

  const deleteScene = useCallback(async (sceneId) => {
    await axios.delete(`${API}/scenes/${sceneId}`);
    sceneStateCache.remove(sceneId);
    setScenes(prev => prev.filter(s => s.id !== sceneId));
    if (currentSceneId === sceneId) setCurrentSceneId(null);
  }, [currentSceneId]);

  // ===== MAP COORDS (account for world transform) =====
  const getMapCoords = useCallback((e) => {
    if (!worldRef.current) return null;
    const rect = worldRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  }, []);

  // Use container coords for spotlight (viewport-relative)
  const getViewportCoords = useCallback((e) => {
    if (!mapRef.current) return null;
    const rect = mapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (draggingToken !== null) {
      const coords = getMapCoords(e);
      if (!coords) return;
      setPlacedTokens(prev => prev.map((t, i) => i === draggingToken ? { ...t, x: coords.x, y: coords.y } : t));
      return;
    }
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }
    if (!isDragging || locked) return;
    const coords = getViewportCoords(e);
    if (!coords) return;
    setSpotlight(coords);
    postMessage({ type: 'MOVE', x: coords.x, y: coords.y });
  }, [locked, isDragging, isPanning, draggingToken, getMapCoords, getViewportCoords, postMessage, panStart]);

  const handleMouseDown = useCallback((e) => {
    // Middle click OR right click OR alt+left = PAN
    if (e.button === 1 || e.button === 2 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
      return;
    }
    if (e.button === 0 && draggingToken === null) {
      setSelectedToken(null);
      setIsDragging(true);
      if (!locked) {
        const coords = getViewportCoords(e);
        if (coords) { setSpotlight(coords); postMessage({ type: 'MOVE', x: coords.x, y: coords.y }); }
      }
    }
  }, [locked, draggingToken, getViewportCoords, postMessage]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsPanning(false);
    if (draggingToken !== null) setDraggingToken(null);
  }, [draggingToken]);

  const handleContextMenu = useCallback((e) => { e.preventDefault(); }, []);

  const handleDoubleClick = useCallback((e) => {
    const coords = getViewportCoords(e);
    if (!coords) return;
    const pingId = Date.now();
    setPings(prev => [...prev, { id: pingId, x: coords.x, y: coords.y }]);
    postMessage({ type: 'PING', x: coords.x, y: coords.y, id: pingId });
    setTimeout(() => setPings(prev => prev.filter(p => p.id !== pingId)), 1200);
  }, [getViewportCoords, postMessage]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => {
      const next = Math.max(0.3, Math.min(5, prev + delta));
      postMessage({ type: 'ZOOM', zoom: next });
      return next;
    });
  }, [postMessage]);

  // Keyboard: A/D rotate, Delete removes, +/- scale
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (selectedToken === null) return;
      if (e.key === 'a' || e.key === 'A') {
        setPlacedTokens(prev => prev.map((t, i) => i === selectedToken ? { ...t, rotation: (t.rotation || 0) - 15 } : t));
      } else if (e.key === 'd' || e.key === 'D') {
        setPlacedTokens(prev => prev.map((t, i) => i === selectedToken ? { ...t, rotation: (t.rotation || 0) + 15 } : t));
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        setPlacedTokens(prev => prev.filter((_, i) => i !== selectedToken));
        setSelectedToken(null);
      } else if (e.key === '=' || e.key === '+') {
        setPlacedTokens(prev => prev.map((t, i) => i === selectedToken ? { ...t, scale: Math.min(5, (t.scale || 1) + 0.1) } : t));
      } else if (e.key === '-' || e.key === '_') {
        setPlacedTokens(prev => prev.map((t, i) => i === selectedToken ? { ...t, scale: Math.max(0.2, (t.scale || 1) - 0.1) } : t));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedToken]);

  const handleMapLoaded = useCallback((url, id) => {
    setMapUrl(url); setMapId(id || ''); setPlacedTokens([]);
    postMessage({ type: 'MAP_UPDATE', mapUrl: url });
    toast.success('Map loaded');
  }, [postMessage]);

  const handlePlaceToken = useCallback((tokenDef) => {
    const states = tokenDef.states || [];
    const firstState = states[0];
    const imageUrl = firstState?.file_id ? `${API}/files/${firstState.file_id}` : (firstState?.image_url || '');
    const newToken = {
      id: `placed-${Date.now()}`, token_id: tokenDef.id, name: tokenDef.name,
      image_url: imageUrl,
      states: states.map(s => ({ ...s, image_url: s.file_id ? `${API}/files/${s.file_id}` : (s.image_url || '') })),
      current_state: 0, x: 50, y: 50, scale: 1, rotation: 0,
      width: tokenDef.width || 50, height: tokenDef.height || 50,
    };
    setPlacedTokens(prev => {
      const next = [...prev, newToken];
      postMessage({ type: 'TOKENS_UPDATE', placedTokens: next });
      return next;
    });
  }, [postMessage]);

  const handleRemoveToken = useCallback((index) => {
    setPlacedTokens(prev => {
      const next = prev.filter((_, i) => i !== index);
      postMessage({ type: 'TOKENS_UPDATE', placedTokens: next });
      return next;
    });
    if (selectedToken === index) setSelectedToken(null);
  }, [postMessage, selectedToken]);

  const handleCycleTokenState = useCallback((index) => {
    setPlacedTokens(prev => prev.map((t, i) => {
      if (i !== index || !t.states || t.states.length < 2) return t;
      const next = ((t.current_state || 0) + 1) % t.states.length;
      const state = t.states[next];
      return { ...t, current_state: next, image_url: state.image_url || `${API}/files/${state.file_id}` };
    }));
  }, []);

  const openPlayerWindow = useCallback(() => {
    playerWindowRef.current = window.open(`${window.location.origin}/player`, 'Mapstr_Player', 'width=1200,height=800,menubar=no,toolbar=no,status=no');
    setTimeout(() => broadcastFullState(), 500);
    setTimeout(() => broadcastFullState(), 1500);
  }, [broadcastFullState]);

  const mapImageUrl = mapUrl ? (mapUrl.startsWith('http') ? mapUrl : `${API}/files/${mapUrl}`) : '';
  const gridRgb = gridColor === 'white' ? '255,255,255' : gridColor === 'gray' ? '128,128,128' : '0,0,0';

  return (
    <TooltipProvider>
      <div className="admin-page" data-testid="admin-page" onContextMenu={handleContextMenu}>
        {/* Top Bar */}
        <div className="fixed top-0 left-0 right-0 z-40 flex items-center gap-3 px-4 py-2 glass-panel rounded-none border-t-0 border-x-0" data-testid="top-bar">
          <Button size="sm" variant="ghost" onClick={() => { saveSession(); navigate('/'); }} className="text-zinc-400 hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm font-semibold text-zinc-300 truncate" style={{ fontFamily: 'Cinzel, serif' }} data-testid="session-name">{sessionName}</span>
          <div className="flex-1" />
          <Button size="sm" onClick={saveSession} className="bg-emerald-600 hover:bg-emerald-500 text-black font-semibold" data-testid="save-btn">
            <Save className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>

        {/* Token Sidebar */}
        <TokenSidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          onPlaceToken={handlePlaceToken}
          onClearAllTokens={() => { setPlacedTokens([]); setSelectedToken(null); postMessage({ type: 'TOKENS_UPDATE', placedTokens: [] }); }}
          placedTokenCount={placedTokens.length}
        />

        {/* Map Viewport */}
        <div
          ref={mapRef}
          className="map-container"
          style={{ paddingTop: '40px', cursor: isPanning ? 'grabbing' : 'crosshair' }}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onDoubleClick={handleDoubleClick}
          onWheel={handleWheel}
          data-testid="map-container"
        >
          {mapImageUrl ? (
            <>
              {/* === WORLD TRANSFORM GROUP (map + grid + tokens move/scale together) === */}
              <div
                ref={worldRef}
                className="absolute inset-0"
                style={{
                  transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
                  transformOrigin: 'center center',
                }}
              >
                {/* Map Image */}
                <img
                  src={mapImageUrl} alt="Battle Map"
                  className="w-full h-full object-contain select-none"
                  draggable={false} data-testid="map-image"
                />

                {/* Grid (inside world transform) */}
                {showGrid && (
                  <div className="absolute inset-0 pointer-events-none" style={{
                    backgroundImage: `linear-gradient(rgba(${gridRgb},${gridOpacity}) 1px, transparent 1px), linear-gradient(90deg, rgba(${gridRgb},${gridOpacity}) 1px, transparent 1px)`,
                    backgroundSize: `${gridSize}px ${gridSize}px`,
                  }} />
                )}

                {/* Tokens (inside world transform — pinned to grid) */}
                {placedTokens.map((token, index) => (
                  <div
                    key={token.id || index}
                    className={`map-token ${draggingToken === index ? 'dragging' : ''} ${selectedToken === index ? 'selected' : ''}`}
                    style={{
                      left: `${token.x}%`, top: `${token.y}%`,
                      width: `${token.width}px`, height: `${token.height}px`,
                      transform: `translate(-50%, -50%) scale(${token.scale || 1}) rotate(${token.rotation || 0}deg)`,
                    }}
                    onMouseDown={(e) => { e.stopPropagation(); setDraggingToken(index); setSelectedToken(index); }}
                    onClick={(e) => { e.stopPropagation(); if (e.detail === 1) setSelectedToken(index); if (e.detail === 2) handleCycleTokenState(index); }}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveToken(index); }}
                    data-testid={`placed-token-${index}`}
                  >
                    <img src={token.image_url} alt={token.name || 'token'} className="w-full h-full object-contain rounded" draggable={false} />
                    {selectedToken === index && (
                      <div className="absolute -inset-1 border-2 border-amber-400 rounded pointer-events-none animate-pulse" />
                    )}
                    {token.states && token.states.length > 1 && (
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-amber-500 text-black text-[8px] font-bold flex items-center justify-center pointer-events-none">
                        {(token.current_state || 0) + 1}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* === VIEWPORT-SPACE ELEMENTS (reticle, pings, fog) === */}
              {/* Reticle */}
              <div className="admin-reticle" style={{ left: `${spotlight.x}%`, top: `${spotlight.y}%`, width: `${radius * 2}px`, height: `${radius * 2}px` }} data-testid="admin-reticle" />

              {/* Pings */}
              {pings.map(ping => (
                <div key={ping.id} className="ping-effect" style={{ left: `${ping.x}%`, top: `${ping.y}%` }}>
                  <div className="ping-ring" /><div className="ping-ring" /><div className="ping-ring" />
                </div>
              ))}
            </>
          ) : (
            <div className="empty-map-state" data-testid="empty-map-state">
              <Map className="w-16 h-16 text-emerald-500/30" />
              <p className="text-lg font-semibold text-zinc-500" style={{ fontFamily: 'Cinzel, serif' }}>No Map Loaded</p>
              <p className="text-sm text-zinc-600">Upload a map image or provide a URL</p>
              <Button onClick={() => setShowMapDialog(true)} className="mt-4 bg-emerald-600 hover:bg-emerald-500 text-black font-semibold" data-testid="load-map-btn-empty">
                <Upload className="w-4 h-4 mr-2" /> Load Map
              </Button>
            </div>
          )}
        </div>

        {/* Bottom Control Panel */}
        <ControlPanel
          radius={radius} onRadiusChange={(v) => { setRadius(v); postMessage({ type: 'RESIZE', radius: v }); }}
          zoom={zoom} onZoomChange={(v) => { setZoom(v); postMessage({ type: 'ZOOM', zoom: v }); }}
          fogOpacity={fogOpacity} onFogOpacityChange={(v) => { setFogOpacity(v); postMessage({ type: 'FOG_OPACITY', fogOpacity: v }); }}
          feather={feather} onFeatherChange={(v) => { setFeather(v); postMessage({ type: 'FEATHER', feather: v }); }}
          gridSize={gridSize} onGridSizeChange={(v) => { setGridSize(v); postMessage({ type: 'GRID_SIZE', gridSize: v }); }}
          gridColor={gridColor} onGridColorChange={(v) => { setGridColor(v); postMessage({ type: 'GRID_COLOR', gridColor: v }); }}
          gridOpacity={gridOpacity} onGridOpacityChange={(v) => { setGridOpacity(v); postMessage({ type: 'GRID_OPACITY', gridOpacity: v }); }}
          spotlightMode={spotlightMode} onSpotlightModeChange={(v) => { setSpotlightMode(v); postMessage({ type: 'SPOTLIGHT_MODE', spotlightMode: v }); }}
          locked={locked} onLockedChange={setLocked}
          showGrid={showGrid} onShowGridChange={setShowGrid}
          spotlight={spotlight}
          onOpenMap={() => setShowMapDialog(true)}
          onOpenPlayerWindow={openPlayerWindow}
          placedTokenCount={placedTokens.length}
          selectedToken={selectedToken !== null ? placedTokens[selectedToken] : null}
          scenes={scenes} currentSceneId={currentSceneId}
          onAddScene={addScene} onSwitchScene={switchScene} onDeleteScene={deleteScene}
        />

        <MapUploadDialog open={showMapDialog} onOpenChange={setShowMapDialog} onMapLoaded={handleMapLoaded} />
        <Toaster theme="dark" position="bottom-right" />
      </div>
    </TooltipProvider>
  );
}
